/**
 * Project Board Kanban — JavaScript design tokens and constants.
 * Mirrors project-board-design-tokens.scss for use in React components.
 */

export const BOARD_LABELS = [
  { id: "red", name: "Bug", color: "#EB5A46" },
  { id: "orange", name: "Urgent", color: "#FF9F1A" },
  { id: "yellow", name: "In Review", color: "#F2D600" },
  { id: "green", name: "Feature", color: "#61BD4F" },
  { id: "blue", name: "Info", color: "#0079BF" },
  { id: "purple", name: "Design", color: "#C377E0" },
  { id: "pink", name: "Marketing", color: "#FF78CB" },
  { id: "sky", name: "Research", color: "#00C2E0" },
  { id: "lime", name: "Done", color: "#51E898" },
  { id: "gray", name: "Backlog", color: "#B3BAC5" },
];

export const BOARD_BACKGROUND_PRESETS = [
  { id: "blue", name: "Ocean", color: "#0079bf75" },
  { id: "orange", name: "Sunset", color: "#D2903475" },
  { id: "green", name: "Forest", color: "#51983975" },
  { id: "red", name: "Brick", color: "#B0463275" },
  { id: "purple", name: "Grape", color: "#89609E75" },
  { id: "pink", name: "Rose", color: "#CD5A9175" },
  { id: "lime", name: "Mint", color: "#4BBF6B75" },
  { id: "sky", name: "Aqua", color: "#00AECC75" },
  { id: "gray", name: "Slate", color: "#838C9175" },
];

export const BOARD_VISIBILITY_OPTIONS = [
  { value: "private", label: "Private", description: "Only board members" },
  { value: "team", label: "Team", description: "All project members" },
  { value: "public", label: "Public", description: "Anyone in the organization" },
];

export const BOARD_DUE_DATE_FILTERS = [
  { value: "", label: "All dates" },
  { value: "overdue", label: "Overdue" },
  { value: "today", label: "Due today" },
  { value: "week", label: "Due this week" },
  { value: "none", label: "No due date" },
];

export const BOARD_LAYOUT = {
  listWidth: 272,
  listMinHeight: 80,
  boardHeaderHeight: 56,
  cardCoverHeight: 160,
  modalWidth: 880,
  modalSidebarWidth: 168,
  avatarSm: 24,
  avatarMd: 32,
  avatarLg: 40,
};

export const BOARD_Z_INDEX = {
  listDrag: 10,
  cardDrag: 20,
  dropdown: 1100,
  modal: 2500,
};

export const getLabelById = (labelId) =>
  BOARD_LABELS.find((label) => label.id === labelId) || null;

export const getAllLabelsForBoard = (boardId, customLabels = []) => [
  ...BOARD_LABELS,
  ...customLabels,
];

export const getLabelsByIds = (labelIds = []) =>
  (Array.isArray(labelIds) ? labelIds : String(labelIds || "").split(","))
    .map((id) => getLabelById(String(id).trim()))
    .filter(Boolean);

export const getBoardBackground = (id) =>
  BOARD_BACKGROUND_PRESETS.find((bg) => bg.id === id) || BOARD_BACKGROUND_PRESETS[0];

/**
 * Due date status for card badges.
 * @returns {'overdue'|'due-soon'|'normal'|'none'}
 */
export const getDueDateStatus = (dueDate) => {
  if (!dueDate) return "none";

  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return "none";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());

  if (dueDay < today) return "overdue";

  const diffMs = dueDay.getTime() - today.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays <= 2) return "due-soon";

  return "normal";
};

export const formatDueDate = (dueDate) => {
  if (!dueDate) return "";
  const date = new Date(dueDate);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dueDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dueDay.getTime() === today.getTime()) return "Today";
  if (dueDay.getTime() === tomorrow.getTime()) return "Tomorrow";

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

export const formatEstimatedHours = (hours) => {
  if (hours == null || hours === "") return "";
  const value = Number(hours);
  if (Number.isNaN(value) || value < 0) return "";
  return Number.isInteger(value) ? `${value}h` : `${value}h`;
};

export const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const AVATAR_COLORS = [
  "#0079BF",
  "#89609E",
  "#D29034",
  "#519839",
  "#B04632",
  "#00AECC",
  "#4BBF6B",
  "#EB5A46",
];

export const getAvatarColor = (userId) => {
  const id = Number(userId) || 0;
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
};
